import type { ComponentDefinition } from '../types';

/* ==========================================================================
   Component catalog. The single source of truth for "what blocks exist".
   New blocks must be registered here AND in `renderer.tsx` (renderer) +
   `serializer.ts` if they need bespoke HTML.
   --------------------------------------------------------------------------
   We keep the catalog deliberately small and reuse blocks across templates
   instead of inventing a per-template schema (e.g. "directions" is the same
   block type whether it shows up on a worksheet or a rubric).
   ========================================================================== */

export const CATALOG: ComponentDefinition[] = [
  /* -------------------- Headings -------------------- */
  {
    type: 'doc-header',
    label: 'Document Header',
    group: 'Headings',
    tier: 'atom',
    description: 'Brand on the left, meta on the right. Usually the first block.',
    rootAllowed: true,
    props: [
      { key: 'brand', label: 'Brand', type: 'text', default: 'Classroom.' },
      { key: 'kicker', label: 'Document Type', type: 'text', default: 'Worksheet' },
      { key: 'metaLine1', label: 'Meta line 1', type: 'text', default: 'Mathematics · Grade 5' },
      { key: 'metaLine2', label: 'Meta line 2', type: 'text', default: 'Unit 3 · Worksheet 3.4' },
    ],
  },
  {
    type: 'eyebrow',
    label: 'Eyebrow',
    group: 'Headings',
    tier: 'atom',
    description: 'Small all-caps tracked label above a hero.',
    rootAllowed: true,
    props: [{ key: 'text', label: 'Text', type: 'text', default: 'Operations & Problem Solving' }],
  },
  {
    type: 'hero',
    label: 'Hero Title',
    group: 'Headings',
    tier: 'atom',
    description: 'The big page title with optional total-points stamp.',
    rootAllowed: true,
    props: [
      { key: 'title', label: 'Title', type: 'text', default: 'Numbers, Operations & Word Problems' },
      { key: 'showStamp', label: 'Show point stamp', type: 'boolean', default: true },
      { key: 'stampValue', label: 'Stamp value', type: 'text', default: '20' },
      { key: 'stampLabel', label: 'Stamp label', type: 'text', default: 'Total Points' },
    ],
  },
  {
    type: 'name-bar',
    label: 'Name / Date Bar',
    group: 'Headings',
    tier: 'atom',
    description: 'Underlined fields for student name, date, period and score.',
    rootAllowed: true,
    props: [
      { key: 'fields', label: 'Field labels (comma separated)', type: 'text', default: 'Name,Date,Period,Score' },
      { key: 'showScore', label: 'Show /20 score box', type: 'boolean', default: true },
      { key: 'scoreOutOf', label: 'Score out of', type: 'number', default: 20, min: 1, max: 1000 },
    ],
  },
  {
    type: 'section-heading',
    label: 'Section Heading',
    group: 'Headings',
    tier: 'atom',
    description: 'Section title with letter prefix (A / B / C…) and optional point pill.',
    rootAllowed: true,
    props: [
      { key: 'letter', label: 'Letter / number prefix', type: 'text', default: 'A' },
      { key: 'title', label: 'Title', type: 'text', default: 'Multiple Choice' },
      { key: 'points', label: 'Points (blank to hide)', type: 'text', default: '4 pts' },
    ],
  },

  /* -------------------- Text -------------------- */
  {
    type: 'paragraph',
    label: 'Paragraph',
    group: 'Text',
    tier: 'rich',
    description: 'Free-form rich text. Bold/italic, lists, images, math.',
    rootAllowed: true,
    props: [{ key: 'html', label: 'Body', type: 'rich', default: '<p>Type your paragraph here.</p>' }],
  },
  {
    type: 'directions',
    label: 'Directions Callout',
    group: 'Text',
    tier: 'rich',
    description: 'Tinted callout box for instructions to the student.',
    rootAllowed: true,
    props: [
      { key: 'label', label: 'Lead label', type: 'text', default: 'Directions:' },
      {
        key: 'html',
        label: 'Body',
        type: 'rich',
        default: '<p>Complete every part. Show your work in the box and write your final answer with the correct units.</p>',
      },
    ],
  },
  {
    type: 'callout',
    label: 'Highlight Callout',
    group: 'Text',
    tier: 'rich',
    description: 'Solid accent block. Good for tips, alerts, or "remember:" notes.',
    rootAllowed: true,
    props: [
      { key: 'tone', label: 'Tone', type: 'select', default: 'accent', options: [
        { label: 'Accent', value: 'accent' },
        { label: 'Soft', value: 'soft' },
        { label: 'Danger', value: 'danger' },
      ] },
      { key: 'html', label: 'Body', type: 'rich', default: '<p><strong>Tip:</strong> Re-read the question before you answer.</p>' },
    ],
  },

  /* -------------------- Layout -------------------- */
  {
    type: 'spacer',
    label: 'Spacer',
    group: 'Layout',
    tier: 'layout',
    description: 'Empty vertical space.',
    rootAllowed: true,
    props: [{ key: 'height', label: 'Height (pt)', type: 'number', default: 18, min: 2, max: 200 }],
  },
  {
    type: 'divider',
    label: 'Divider',
    group: 'Layout',
    tier: 'layout',
    description: 'Horizontal rule using the current theme.',
    rootAllowed: true,
    props: [
      { key: 'thickness', label: 'Thickness', type: 'select', default: 'thin', options: [
        { label: 'Hairline', value: 'thin' },
        { label: 'Bold', value: 'bold' },
      ] },
    ],
  },
  {
    type: 'work-area',
    label: 'Work Area',
    group: 'Layout',
    tier: 'layout',
    description: 'Empty box with optional ruled lines for the student to write in.',
    rootAllowed: true,
    props: [
      { key: 'lines', label: 'Lines', type: 'number', default: 4, min: 0, max: 30 },
      { key: 'height', label: 'Height (pt)', type: 'number', default: 100, min: 30, max: 600 },
      { key: 'label', label: 'Footer label (blank to hide)', type: 'text', default: 'Answer' },
    ],
  },
  {
    type: 'columns',
    label: 'Columns',
    group: 'Layout',
    tier: 'container',
    description: 'Place blocks side by side in equal-width columns.',
    rootAllowed: true,
    allowedChildren: ['paragraph', 'directions', 'callout', 'work-area', 'question-list', 'choice-list'],
    addChildLabel: 'Add column block',
    defaultChildType: 'paragraph',
    props: [
      { key: 'columns', label: 'Columns', type: 'number', default: 2, min: 1, max: 4 },
      { key: 'gap', label: 'Gap (pt)', type: 'number', default: 16, min: 4, max: 60 },
    ],
  },

  /* -------------------- Lists -------------------- */
  {
    type: 'question-list',
    label: 'Numbered Questions',
    group: 'Lists',
    tier: 'container',
    description: 'Numbered list of questions with optional sub-prompts.',
    rootAllowed: true,
    allowedChildren: ['question'],
    addChildLabel: 'Add question',
    defaultChildType: 'question',
    props: [{ key: 'startAt', label: 'Start numbering at', type: 'number', default: 1, min: 1 }],
  },
  {
    type: 'question',
    label: 'Question',
    group: 'Lists',
    tier: 'rich',
    description: 'Single question prompt with answer line(s).',
    props: [
      { key: 'prompt', label: 'Prompt', type: 'rich', default: '<p>Write your question here.</p>' },
      { key: 'answerStyle', label: 'Answer style', type: 'select', default: 'line', options: [
        { label: 'Single answer line', value: 'line' },
        { label: 'Ruled work area', value: 'lines' },
        { label: 'Empty box', value: 'box' },
        { label: 'No answer space', value: 'none' },
      ] },
      { key: 'answerLines', label: 'Answer lines (when ruled)', type: 'number', default: 3, min: 1, max: 12 },
    ],
  },
  {
    type: 'choice-list',
    label: 'Multiple Choice',
    group: 'Lists',
    tier: 'container',
    description: 'A, B, C, D bubbles. Each child holds its own prompt.',
    rootAllowed: true,
    allowedChildren: ['choice'],
    addChildLabel: 'Add option',
    defaultChildType: 'choice',
    props: [{ key: 'columns', label: 'Columns', type: 'number', default: 2, min: 1, max: 4 }],
  },
  {
    type: 'choice',
    label: 'Choice',
    group: 'Lists',
    tier: 'atom',
    description: 'One bubble + one short label.',
    props: [
      { key: 'label', label: 'Label', type: 'text', default: '4,750' },
      { key: 'correct', label: 'Mark correct (answer key)', type: 'boolean', default: false },
    ],
  },

  /* -------------------- Cards -------------------- */
  {
    type: 'card-grid',
    label: 'Card Grid',
    group: 'Cards',
    tier: 'container',
    description: 'Grid of flashcards / task cards. Configure rows and columns.',
    rootAllowed: true,
    allowedChildren: ['card'],
    addChildLabel: 'Add card',
    defaultChildType: 'card',
    props: [
      { key: 'columns', label: 'Columns', type: 'number', default: 2, min: 1, max: 4 },
      { key: 'rows', label: 'Rows', type: 'number', default: 2, min: 1, max: 6 },
      { key: 'cardHeight', label: 'Card height (pt)', type: 'number', default: 140, min: 60, max: 400 },
    ],
  },
  {
    type: 'card',
    label: 'Card',
    group: 'Cards',
    tier: 'rich',
    description: 'Single card -- short title + body.',
    props: [
      { key: 'tag', label: 'Tag', type: 'text', default: 'Term · 01' },
      { key: 'title', label: 'Title', type: 'text', default: 'Mitosis' },
      { key: 'body', label: 'Body', type: 'rich', default: '<p>The process by which a cell divides…</p>' },
      { key: 'foot', label: 'Footer label', type: 'text', default: 'Side A' },
    ],
  },
  {
    type: 'bingo-grid',
    label: 'Bingo Grid',
    group: 'Cards',
    tier: 'layout',
    description: 'Square grid of word/number cells. Use commas to fill cells in order.',
    rootAllowed: true,
    props: [
      { key: 'size', label: 'Grid size', type: 'number', default: 5, min: 3, max: 7 },
      { key: 'freeCenter', label: 'Free center cell', type: 'boolean', default: true },
      {
        key: 'cells',
        label: 'Cells (comma separated)',
        type: 'text',
        default: 'add,sum,plus,less,subtract,product,times,quotient,divide,half,double,equal,equation,multiply,minus,more,total,fraction,decimal,digit,fact,part,whole,unit,zero',
      },
    ],
  },

  /* -------------------- Assessment -------------------- */
  {
    type: 'rubric',
    label: 'Rubric Table',
    group: 'Assessment',
    tier: 'container',
    description: 'A 4-point analytic rubric. Each child is a criterion row.',
    rootAllowed: true,
    allowedChildren: ['rubric-row'],
    addChildLabel: 'Add criterion',
    defaultChildType: 'rubric-row',
    props: [
      { key: 'scale', label: 'Scale labels (comma separated)', type: 'text', default: '4 · Exemplary,3 · Proficient,2 · Developing,1 · Beginning' },
    ],
  },
  {
    type: 'rubric-row',
    label: 'Rubric Criterion',
    group: 'Assessment',
    tier: 'atom',
    description: 'One row in a rubric.',
    props: [
      { key: 'criterion', label: 'Criterion', type: 'text', default: 'Thesis' },
      { key: 'd4', label: '4 · Exemplary', type: 'text', default: 'Clear, original, defensible thesis.' },
      { key: 'd3', label: '3 · Proficient', type: 'text', default: 'Clear, defensible thesis.' },
      { key: 'd2', label: '2 · Developing', type: 'text', default: 'Thesis present but vague.' },
      { key: 'd1', label: '1 · Beginning', type: 'text', default: 'Missing or off-topic.' },
    ],
  },
  {
    type: 'agenda',
    label: 'Agenda',
    group: 'Assessment',
    tier: 'container',
    description: 'Time-stamped lesson agenda rows.',
    rootAllowed: true,
    allowedChildren: ['agenda-row'],
    addChildLabel: 'Add row',
    defaultChildType: 'agenda-row',
    props: [],
  },
  {
    type: 'agenda-row',
    label: 'Agenda Row',
    group: 'Assessment',
    tier: 'atom',
    description: 'One step in the lesson plan.',
    props: [
      { key: 'time', label: 'Time', type: 'text', default: '0:08' },
      { key: 'name', label: 'Step', type: 'text', default: 'Direct teach' },
      { key: 'desc', label: 'Description', type: 'text', default: 'Mini-lecture & demo' },
    ],
  },
];

export const CATALOG_BY_TYPE: Record<string, ComponentDefinition> = Object.fromEntries(
  CATALOG.map((c) => [c.type, c]),
);

export function getDefinition(type: string): ComponentDefinition | undefined {
  return CATALOG_BY_TYPE[type];
}

export function defaultProps(type: string): Record<string, unknown> {
  const def = getDefinition(type);
  if (!def) return {};
  const out: Record<string, unknown> = {};
  for (const p of def.props) {
    if (p.default !== undefined) out[p.key] = p.default;
  }
  return out;
}
